/*
 * LoginPage — Canvas-Hintergrund + HTML-Overlay mit Google Sign-In Button
 *
 * Zeigt die Login-Seite im Terminal-Stil:
 * - Canvas: Logo, Tagline (gerenderter Text)
 * - HTML-Overlay: Google Sign-In Button (Branding-Guidelines erfordern echten Button)
 * - Footer: Legal-Links
 */

import { SESSION_EXPIRED_KEY } from './AuthService.js'

export class LoginPage {
    /**
     * @param {import('../renderer.js').Renderer} renderer - Canvas-Renderer
     */
    constructor(renderer) {
        this._renderer = renderer
        this._overlay = null   // HTML-Overlay Element
    }

    /**
     * Zeigt die Login-Seite an.
     * Canvas wird mit Logo/Tagline gerendert, HTML-Overlay fuer Google-Button.
     */
    show() {
        // Body-BG auf Schwarz: Renderer._updateColors() hat den Body bereits
        // auf die DEFAULT_DISPLAY_MODE-Farbe gesetzt (z.B. Worker Dark =
        // dunkelblau). Das Canvas ist im Viewport zentriert, der Body-Rand
        // schaut drumherum durch → ohne Override sieht man den Mode-BG statt
        // unseres reinen Schwarz.
        if (typeof document !== 'undefined' && document.body) {
            document.body.style.backgroundColor = '#000000'
        }
        this._renderCanvas()
        this._createHtmlOverlay()
    }

    /**
     * Entfernt die Login-Seite.
     */
    hide() {
        if (this._overlay) {
            this._overlay.remove()
            this._overlay = null
        }
    }

    /**
     * Rendert den Canvas-Hintergrund mit Logo und Tagline.
     */
    _renderCanvas() {
        const canvas = this._renderer._canvas
        if (!canvas) return

        const ctx = canvas.getContext('2d')
        const w = canvas.width
        const h = canvas.height

        // Hintergrund
        ctx.fillStyle = '#000000'
        ctx.fillRect(0, 0, w, h)

        // Logo-Text (gross)
        ctx.fillStyle = '#e0e0e0'
        ctx.font = '32px "Dina", "Courier New", monospace'
        ctx.textAlign = 'center'
        ctx.fillText('CARBISDALE', w / 2, h * 0.30)

        // Tagline
        ctx.fillStyle = '#888888'
        ctx.font = '14px "Dina", "Courier New", monospace'
        ctx.fillText('A text editor with terminal aesthetics', w / 2, h * 0.30 + 40)

        // Trennlinie
        const lineY = h * 0.30 + 60
        ctx.strokeStyle = '#333355'
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.moveTo(w * 0.3, lineY)
        ctx.lineTo(w * 0.7, lineY)
        ctx.stroke()
    }

    /**
     * Erstellt das HTML-Overlay mit Google Sign-In Button und Footer.
     */
    _createHtmlOverlay() {
        // Altes Overlay entfernen falls vorhanden
        this.hide()

        const overlay = document.createElement('div')
        overlay.id = 'login-overlay'
        overlay.style.cssText = `
            position: fixed;
            top: 0; left: 0; right: 0; bottom: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            pointer-events: none;
        `

        // Icon
        const icon = document.createElement('img')
        icon.src = 'assets/images/writelite_icon_512_NERD.png'
        icon.alt = 'Writelite'
        icon.style.cssText = `
            width: 128px;
            height: 128px;
            margin-top: 40px;
            pointer-events: none;
        `
        overlay.appendChild(icon)

        // Google Sign-In Button Container
        const buttonContainer = document.createElement('div')
        buttonContainer.style.cssText = `
            margin-top: 40px;
            pointer-events: auto;
        `

        // Google Sign-In Button (gemäss Google Branding Guidelines)
        const loginButton = document.createElement('a')
        loginButton.href = '/api/auth/login'
        loginButton.style.cssText = `
            display: inline-flex;
            align-items: center;
            gap: 12px;
            padding: 12px 24px;
            background: #ffffff;
            color: #3c4043;
            border: 1px solid #dadce0;
            border-radius: 4px;
            font-family: 'Roboto', 'Segoe UI', Arial, sans-serif;
            font-size: 14px;
            font-weight: 500;
            text-decoration: none;
            cursor: pointer;
            transition: background-color 0.2s, box-shadow 0.2s;
            box-shadow: 0 1px 2px rgba(0,0,0,0.1);
        `
        loginButton.addEventListener('mouseenter', () => {
            loginButton.style.backgroundColor = '#f8f9fa'
            loginButton.style.boxShadow = '0 1px 3px rgba(0,0,0,0.2)'
        })
        loginButton.addEventListener('mouseleave', () => {
            loginButton.style.backgroundColor = '#ffffff'
            loginButton.style.boxShadow = '0 1px 2px rgba(0,0,0,0.1)'
        })

        // Google "G" Logo (SVG inline)
        const gLogo = document.createElement('span')
        gLogo.innerHTML = `<svg width="18" height="18" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>`
        gLogo.style.display = 'flex'

        loginButton.appendChild(gLogo)

        const isDE = (navigator.language || '').toLowerCase().startsWith('de')
        loginButton.appendChild(document.createTextNode(
            isDE ? 'Mit Google anmelden' : 'Sign in with Google'
        ))
        buttonContainer.appendChild(loginButton)

        // Session-Ablauf-Hinweis: erklaert den Reload mitten in der Arbeit
        // (handleSessionLost setzt das Flag vor dem window.location.reload).
        let sessionExpired = false
        try {
            sessionExpired = localStorage.getItem(SESSION_EXPIRED_KEY) === '1'
            if (sessionExpired) localStorage.removeItem(SESSION_EXPIRED_KEY)
        } catch { /* ignore */ }
        if (sessionExpired) {
            const notice = document.createElement('p')
            notice.style.cssText = `
                margin-bottom: 20px;
                max-width: 340px;
                text-align: center;
                font-family: 'Courier New', monospace;
                font-size: 13px;
                color: #e0b040;
                line-height: 1.5;
            `
            notice.textContent = isDE
                ? 'Deine Sitzung ist abgelaufen — bitte melde Dich erneut an. Deine Texte sind auf diesem Gerät gespeichert.'
                : 'Your session has expired — please sign in again. Your texts are saved on this device.'
            overlay.appendChild(notice)
        }

        overlay.appendChild(buttonContainer)

        // ToS-Hinweis unter dem Button
        const tosNotice = document.createElement('p')
        tosNotice.style.cssText = `
            margin-top: 16px;
            max-width: 300px;
            text-align: center;
            font-family: 'Courier New', monospace;
            font-size: 11px;
            color: #888888;
            line-height: 1.5;
            pointer-events: auto;
        `
        const tosLink = document.createElement('a')
        tosLink.href = '/legal/terms.html'
        tosLink.target = '_blank'
        tosLink.textContent = isDE ? 'Nutzungsbedingungen' : 'Terms of Service'
        tosLink.style.cssText = 'color: #33cc33; text-decoration: none;'

        const privacyLink = document.createElement('a')
        privacyLink.href = '/legal/privacy.html'
        privacyLink.target = '_blank'
        privacyLink.textContent = isDE ? 'Datenschutzerklärung' : 'Privacy Policy'
        privacyLink.style.cssText = 'color: #33cc33; text-decoration: none;'

        if (isDE) {
            tosNotice.append('Mit der Anmeldung akzeptierst du unsere ', tosLink, ' und ', privacyLink, '.')
        } else {
            tosNotice.append('By signing in, you agree to our ', tosLink, ' and ', privacyLink, '.')
        }
        overlay.appendChild(tosNotice)

        // Footer mit Legal-Links
        const footer = document.createElement('div')
        footer.style.cssText = `
            position: fixed;
            bottom: 20px;
            display: flex;
            gap: 20px;
            pointer-events: auto;
        `
        const links = [
            { text: isDE ? 'Datenschutz' : 'Privacy', href: '/legal/privacy.html' },
            { text: isDE ? 'Impressum' : 'Imprint', href: '/legal/impressum.html' }
        ]

        for (const link of links) {
            const a = document.createElement('a')
            a.href = link.href
            a.textContent = link.text
            a.target = '_blank'
            a.style.cssText = `
                color: #33cc33;
                font-family: 'Courier New', monospace;
                font-size: 12px;
                text-decoration: none;
                opacity: 0.6;
            `
            a.addEventListener('mouseenter', () => { a.style.opacity = '1.0' })
            a.addEventListener('mouseleave', () => { a.style.opacity = '0.6' })
            footer.appendChild(a)
        }

        overlay.appendChild(footer)

        // Zum DOM hinzufuegen
        document.body.appendChild(overlay)
        this._overlay = overlay
    }
}

/**
 * Zeigt eine Blocked-Seite an (Account gesperrt).
 */
export function showBlockedPage(renderer) {
    const canvas = renderer._canvas
    if (!canvas) return

    // Body-BG auf Schwarz (s. LoginPage.show — Mode-BG zieht sonst durch).
    if (typeof document !== 'undefined' && document.body) {
        document.body.style.backgroundColor = '#000000'
    }

    const ctx = canvas.getContext('2d')
    const w = canvas.width
    const h = canvas.height

    ctx.fillStyle = '#000000'
    ctx.fillRect(0, 0, w, h)

    ctx.fillStyle = '#cc4444'
    ctx.font = '24px "Dina", "Courier New", monospace'
    ctx.textAlign = 'center'
    ctx.fillText('Account gesperrt', w / 2, h * 0.4)

    ctx.fillStyle = '#888888'
    ctx.font = '14px "Dina", "Courier New", monospace'
    ctx.fillText('Kontaktiere support@writelite.app', w / 2, h * 0.4 + 40)
}
